import { ENDPOINTS } from '@api/endpoints';
import { authFetch } from '@api/authFetch';
import { API_BASE_URL } from '@config/constants';

export type NotificationKind =
  | 'newsletter_submitted'
  | 'newsletter_approved'
  | 'newsletter_sent_back'
  | 'newsletter_published';

export interface BackendNotification {
  id: string;
  kind: NotificationKind;
  message: string;
  description?: string | null;
  link?: string | null;
  newsletter_id?: string | null;
  read: boolean;
  /** ISO datetime string. */
  created_at: string;
}

export interface NotificationListPayload {
  items: BackendNotification[];
  unread_count: number;
}

async function parseError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({}));
  return (
    (body as { detail?: string }).detail ||
    (body as { message?: string }).message ||
    `${fallback}: ${res.status}`
  );
}

export async function listRecent(limit = 20): Promise<NotificationListPayload> {
  const url = `${API_BASE_URL}${ENDPOINTS.NOTIFICATIONS.LIST}?limit=${limit}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error(await parseError(res, 'Failed to list notifications'));
  const json = await res.json();
  return json.data as NotificationListPayload;
}

export async function getUnreadCount(): Promise<number> {
  const res = await authFetch(`${API_BASE_URL}${ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT}`);
  if (!res.ok) throw new Error(await parseError(res, 'Failed to fetch unread count'));
  const json = await res.json();
  return (json.data?.count ?? 0) as number;
}

export async function markRead(id: string): Promise<number> {
  const res = await authFetch(`${API_BASE_URL}${ENDPOINTS.NOTIFICATIONS.MARK_READ(id)}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await parseError(res, 'Failed to mark notification as read'));
  const json = await res.json();
  return (json.data?.count ?? 0) as number;
}

export async function markAllReadRequest(): Promise<number> {
  const res = await authFetch(`${API_BASE_URL}${ENDPOINTS.NOTIFICATIONS.READ_ALL}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await parseError(res, 'Failed to mark all notifications as read'));
  const json = await res.json();
  return (json.data?.count ?? 0) as number;
}
